import type { GQL } from "@binale-tech/shared";

import { withThinking } from "../runner/runAgent";
import type { AgentStep, ReportLine } from "../runner/types";

export const aiCheckStep: AgentStep = {
    id: "aiCheck",
    needs: (ctx) => ctx.created.recordIds.length > 0,
    async run(ctx, io) {
        const byProduct = new Map<GQL.IProductKey, string[]>();
        for (const id of ctx.created.recordIds) {
            const product = ctx.recordInfo(id)?.product;
            if (!product) continue;
            if (!ctx.canWrite(product)) {
                io.narrateSkipped(product);
                continue;
            }
            byProduct.set(product, [...(byProduct.get(product) ?? []), id]);
        }
        if (byProduct.size === 0) return { status: "nothing" };

        let checked = 0;
        let invalid = 0;
        let notChecked = 0;
        for (const [product, ids] of byProduct) {
            if (io.isCancelled()) break;
            // the mutation commits before it returns, so the server read below sees the new review
            const reviews = await withThinking(io, "app.agent.aiCheck.thinking", async () => {
                await ctx.runAiReview(product, ids);
                return ctx.readAiReviews(product, ids);
            });
            // a null review means the record is ineligible (no single linked AI log)
            const returned = new Set(reviews.map((r) => r.id));
            notChecked += ids.filter((id) => !returned.has(id)).length;
            for (const { aiReview } of reviews) {
                if (!aiReview) continue;
                checked += 1;
                if (!aiReview.isValid) invalid += 1;
            }
        }
        const open: ReportLine[] = [];
        // ids the server did not return (other year, deleted) were not checked
        if (notChecked > 0) open.push({ textId: "app.agent.report.aiCheckNotChecked", values: { count: notChecked } });
        if (checked === 0) return open.length > 0 ? { status: "done", open } : { status: "nothing" };

        io.post({ kind: "done", textId: "app.agent.aiCheck.done", values: { checked, invalid } });
        if (invalid > 0) open.unshift({ textId: "app.agent.report.aiCheckInvalid", values: { count: invalid } });
        return {
            status: "done",
            summary: [{ textId: "app.agent.report.aiChecked", values: { count: checked } }],
            open,
        };
    },
};
